import {
  BaseModel,
  Image,
  SipDomain,
  SessionType,
  CampaignAutoResponderTrigger,
  TicketSystem,
  OfferIntents,
  CancelItemsSelection,
} from './index';

export enum CampaignStatus {
  Inactive = 0,
  Active = 1,
  Disabled = 2,
}

export enum RestockFeeType {
  Fixed = 1,
  Percent = 2,
}

export enum RestockFeeCalcType {
  Max = 1,
  Sum = 2,
}

export enum VerificationMethod {
  FullName = 1,
  LastName = 2,
  Street = 3,
  Zip = 4,
  Address = 5,
  BillingStreet = 6,
  BillingZip = 7,
  BillingAddress = 8,
  PhoneOrEmail = 9,
  PartialPhoneOrEmail = 10,

  //special values
  StreetAndZip = 100000,
}

export enum ContactType {
  Support = 0,
  Sales = 1,
  RMA = 2,
  Cancel = 3,
}

export const ContactTypeLabels = ['Support', 'Sales', 'Returns', 'Cancellations'];

export enum LifelinePhoneSetting {
  WhenCallCenterOpen = 0,
  Never = 1,
  Always = 2,
  PhoneSessionOnly = 3,
  PhoneSessionAndCallCenterOpen = 4,
}

export const LifelinePhoneSettingOptions = [
  { value: LifelinePhoneSetting.WhenCallCenterOpen, label: 'Show When Call Center is Open' },
  { value: LifelinePhoneSetting.Never, label: 'Never Show' },
  { value: LifelinePhoneSetting.Always, label: 'Always Show' },
  { value: LifelinePhoneSetting.PhoneSessionOnly, label: 'Show Only For Phone Sessions' },
  {
    value: LifelinePhoneSetting.PhoneSessionAndCallCenterOpen,
    label: 'Show Only For Phone Sessions and When Call Center is Open',
  },
];

export enum ShowMarketplaceSetting {
  HideIfEmbedded = 0,
  Never = 1,
  Always = 2,
}

export const ShowMarketplaceSettingOptions = [
  { value: ShowMarketplaceSetting.HideIfEmbedded, label: 'Hide if Embedded' },
  { value: ShowMarketplaceSetting.Never, label: 'Never Show' },
  { value: ShowMarketplaceSetting.Always, label: 'Always Show' },
];

export enum MembershipType {
  SpecificSubscription = 1,
  AnyActiveSubscription = 2,
  AnyCompletedPurchase = 3,
  ExistingCustomer = 4,
}

export enum ShowDeclinedOrders {
  FailedRebillsOnly = 1,
  Always = 2,
  Never = 3,
}

export enum ShowBreadcrumbs {
  OnlyIfCanUndo = 1,
  Always = 2,
  Never = 3,
}

export enum MFAType {
  Disabled = 1,
  EmailPreferred = 2,
  SMSPreferred = 3,
  BestAvailable = 4,
  UserChoice = 5,
}

export enum SMTPTransportType {
  PlainText = 0,
  SSL = 1,
  TLS = 2,
}

export enum AutoCancelUpsellType {
  Disabled = 1,
  AfterAllMainProducts = 2,
  AfterAnyMainProduct = 3,
}

export enum LinkMatchSeting {
  Disabled = 1,
  Match = 2,
  PreMatch = 3,
  PreMatchStrict = 4,
}

export interface SMTPConfig {
  host: string;
  port: number;
  username: string;
  password: string;
  transport_type: SMTPTransportType;
  mail_from: string;
}

export interface CallCenterHours {
  dow: number;
  starttime: string;
  endtime: string;
}

export enum DefaultContactFormFields {
  FirstName = 'First Name',
  LastName = 'Last Name',
  ContactNumber = 'Contact Number',
  Email = 'Email',
  Message = 'Message',
}

export enum LinkCustomerMatchMethod {
  CustomerId = 1,
  Email = 2,
  Phone = 3,
}

export const LinkCustomerMatchMethodLabels = [
  '', // 0, unused
  'Customer ID', // 1
  'Email', // 2
  'Phone', // 3
]

export interface ContactFormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  values?: string[];
}

export interface PhoneContact extends BaseModel {
  contact_type: ContactType;
  phone: string;
}

export interface NotificationContact extends BaseModel {
  contact_type: ContactType;
  email: string;
  phone_sms: string;
}

export interface CustomDomain extends BaseModel {
  campaign: string | number;
  domain: string;
  is_verified: boolean;
  verified: string;
  cname_target: string;
  type: SessionType;
  name: string;
  visual_entry_funnels: string[] | number[];
  show_phone_link: LifelinePhoneSetting;
  created: string;
  support_phone: string;
  is_react: boolean;
}

export interface QuickLink extends BaseModel {
  type: QuickLinkType;
  title: string;
  link?: string;
  subtitle: string;
  step_key: string;
  external_url: string;
}

export interface ChatInactivityMessage extends BaseModel {
  message: string;
  duration: number;
}

export interface PortalSettings extends BaseModel {
  min_billing_cycle_for_cancel: number;
  show_fulfillment_status: boolean;
  show_subscription_status: boolean;
  show_tracking_in_list: boolean;
  show_tracking_in_details: boolean;
  show_transactions_in_list: boolean;
  show_transactions_in_details: boolean;
  min_billing_cycle_for_cancel_subscription: number;
  allow_edit_payment_method: boolean;
  allow_edit_shipping_address: boolean;
  allow_edit_billing_interval: boolean;
  max_pause_days: number;
  allow_revision: boolean;
  show_next_bill_date: boolean;
  allow_reactivate_subscription: boolean;
  show_price_in_list: boolean;
  show_price_in_details: boolean;
  show_taxes: boolean;
  show_shipping_price: boolean;
  show_payment_status_in_list: boolean;
  show_payment_status_in_details: boolean;
  allow_revise_subscription: boolean;
  allow_cancel_trial: boolean;
  allow_cancel_subscription_trial: boolean;
  allow_return_trial: boolean;
  allow_extend_trial: boolean;
  allow_adjust_trial_frequency: boolean;
  allow_bill_trial_now: boolean;
  allow_return: boolean;
  allow_return_subscription: boolean;
  holdoff_actions_config: { [key: string]: OfferIntents[] };
  allow_buy_again: boolean;
  auto_select_single_item: boolean;
  hidden_fees_label: string;
  cancel_items_selection: CancelItemsSelection;
  return_items_selection: CancelItemsSelection;
  lifeline_timeout: number;
  lifeline_undo_count: number;
  lifeline_unmatched_count: number;
  lifeline: string | number;
  error_lifeline: string | number;
  exit_lifeline: string | number;
  search_lifeline: string | number;
  unmatched_lifeline: string | number;
  cancel_subscription_min_days: number;
  subscription_lifeline: string | number;
  show_offer_details: boolean;
  show_shipping_processing: boolean;
  show_return_pending: boolean;
  show_resolve_refund: boolean;
  // Order Management Portal — per-page section lists: { home: [{key,data}], subscriptions: [...], ... }
  sections_config?: { [page: string]: { key: string; data?: any }[] };
}

export interface Campaign extends BaseModel {
  name: string;
  subdomain: string;
  active_domain: string;
  display_name: string;
  email: string;
  support_phone: string;
  support_email: string;
  email_enabled: boolean;
  email_forward_delay: number;
  support_email_fields: ContactFormField[];
  status: CampaignStatus;
  mfa_type: MFAType;
  link_match: LinkMatchSeting;
  matched_verify_all: boolean;
  unmatched_verify_all: boolean;
  match_phone: boolean;
  match_email: boolean;
  match_order_number: boolean;
  is_verified: boolean;
  categories: string[] | number[];
  styles: string[] | number[];
  visual_entry_funnels: string[] | number[];
  voice_entry_funnels: string[] | number[];
  sms_entry_funnels: string[] | number[];
  email_templates: string[] | number[];
  autoresponder_triggers: CampaignAutoResponderTrigger[];
  logo: Image;
  mobile_logo: Image;
  favicon: Image | string | number;
  processing_gif: Image;
  url: string;
  matched_verification_methods: VerificationMethod[];
  unmatched_verification_methods: VerificationMethod[];
  timezone: string;
  call_center_hours: CallCenterHours[];
  crm_campaigns: string[] | number[];
  faq_topic: string | number | null;
  campaign_products: string[] | number[];
  phone_loop_count: number;
  phone_input_timeout: number;
  restock_fee: number;
  restock_fee_type: RestockFeeType;
  restock_fee_calc_type: RestockFeeCalcType;
  rma_shipping_fee: number;
  return_days: number;
  exchange_days: number;
  max_extra_return_days: number;
  auto_cancel_upsells: AutoCancelUpsellType;
  phone_contacts: PhoneContact[];
  notification_contacts: NotificationContact[];
  return_action: string | number;
  exchange_action: string | number;
  cancel_action: string | number;
  cancel_subscription_action: string | number;
  is_hybrid: boolean;
  hybrid_entry_funnels: string[] | number[];
  filtered_campaigns: string[] | number[];
  custom_domains: CustomDomain[];
  lifeline_phone_setting: LifelinePhoneSetting;
  sip_domain: SipDomain;
  smtp_config: SMTPConfig;
  max_order_days: number;
  always_show_active_orders: boolean;
  subdomain_type: SessionType;
  membership_type: MembershipType;
  membership_discount: number;
  action_holdoff_delay: number;
  show_declined_orders: ShowDeclinedOrders;
  show_breadcrumbs: ShowBreadcrumbs;
  call_recording_enabled: boolean;
  call_frequency_timeout: number;
  call_frequency_threshold: number;
  telnyx_sip_uri: string;
  call_frequency_afterhours: boolean;
  sms_domain: string;
  show_phone_link: LifelinePhoneSetting;
  blacklisted_states: string[];
  ticket_system: TicketSystem;
  show_marketplace: ShowMarketplaceSetting;
  guarantee_days: number;
  show_replaced_items: boolean;
  shipping_system: string | number;
  alpha_sender_id: string;
  chatbot_enabled: boolean;
  chatbot_icon: Image;
  session_start_ticket_delay: number;
  session_incomplete_ticket_delay: number;
  allowed_countries: string[];
  mouseflow_enabled: boolean;
  quick_links?: QuickLink[];
  chat_inactivity_messages?: ChatInactivityMessage[];
  show_order_summary: boolean;
  bank_url: string;
  verify_attempts: number;
  login_attempts: number;
  customer_lock_time: number;
  portal_settings: PortalSettings;
  customer_portal_id: string | number;
  portal_creation_failed?: boolean;
  single_step_funnels: string[] | number[];
}

export interface CampaignSummary {
  crms: Array<object>;
  support_funnels: Array<object>;
  unassigned_campaigns: number;
}

export interface PhoneEntryFunnelConfig {
  welcome_file: string | number;
  welcome_prompt: string;
  after_hours_file: string | number;
  after_hours_prompt: string;
  mobile_file: string | number;
  mobile_prompt: string;
  thankyou_file: string | number;
  thankyou_prompt: string;
  voice_type: string;
  sms_message: string;
  respond_to_sms: boolean;
  autoresponder: string | number;
}

export interface CampaignMenu {
  campaign: Campaign | null;
  activeSection: string;
  isHybrid: boolean;
}

export enum CampaignSection {
  General = 'general',
  Branding = 'branding',
  Domains = 'domains',
  CallCenter = 'call-center',
  PhoneSetup = 'phone-setup',
  EmailSetup = 'email-setup',
  Autoresponder = 'autoresponders',
  LoginSettings = 'login-settings',
  PolicyAndTerms = 'policy',
  DeploymentPage = 'deployment',
  CustomerPortalSettings = 'customer-portal-settings',
  MarketplaceSettings = 'marketplace-settings',
  CRMCampaigns = 'crm-campaigns',
}

export const CampaignSideBarSections = [
  { id: CampaignSection.General, label: 'General' },
  { id: CampaignSection.Branding, label: 'Branding' },
  { id: CampaignSection.CRMCampaigns, label: 'Campaigns' },
  { id: CampaignSection.Domains, label: 'Domains' },
  { id: CampaignSection.CallCenter, label: 'Call Center' },
  { id: CampaignSection.PhoneSetup, label: 'Phone Settings' },
  { id: CampaignSection.EmailSetup, label: 'Email Settings' },
  { id: CampaignSection.Autoresponder, label: 'Session Recovery' },
  { id: CampaignSection.LoginSettings, label: 'Login Settings' },
  { id: CampaignSection.CustomerPortalSettings, label: 'Portal Settings' },
  { id: CampaignSection.PolicyAndTerms, label: 'Policy And Terms' },
  { id: CampaignSection.DeploymentPage, label: 'Deployment Page' },
  { id: CampaignSection.MarketplaceSettings, label: 'Marketplace Settings' },
];

export const CampaignMenuSections = {
  normal: [
    { id: 'edit', label: 'Settings' },
    { id: 'call-center', label: 'Call Center' },
    { id: 'phone', label: 'Phone Setup' },
    { id: 'email', label: 'Email Setup' },
    { id: 'products', label: 'Campaigns' },
    { id: 'paths', label: 'Paths' },
    { id: 'marketplace', label: 'Marketplace' },
    { id: 'styles', label: 'Themes' },
    { id: 'summary', label: 'Summary' },
  ],
  hybrid: [
    { id: 'edit', label: 'Settings' },
    { id: 'call-center', label: 'Call Center' },
    { id: 'phone', label: 'Phone Setup' },
    { id: 'campaigns', label: 'Brands' },
    { id: 'paths', label: 'Paths' },
    { id: 'styles', label: 'Themes' },
    { id: 'summary', label: 'Summary' },
  ],
};

export function getNextMenuSectionId(menuType: string, sectionId: string) {
  const index = CampaignMenuSections[menuType].findIndex((section) => section.id === sectionId) + 1;

  if (index > 0 && index < CampaignMenuSections[menuType].length) {
    return CampaignMenuSections[menuType][index].id;
  }

  return null;
}

export interface TrackingSource extends BaseModel {
  campaign: string | number;
  source: string;
}

export const CampaignVariables = {
  brand_name: 'Brand Name',
  support_email: 'Support Email',
  support_phone: 'Support Phone Number',
};

export function getCampaignVariables() {
  let items = [];

  Object.keys(CampaignVariables).forEach((key) => {
    items.push({ value: '{' + key + '}', text: CampaignVariables[key] });
  });

  return items;
}

export enum QuickLinkType {
  ALL = 1,
  MATCHED_SESSION = 2,
  UNMATCHED_SESSION = 3,
}

export const QuickLinkTypeLabels = {
  ALL: 'All - Matched/Unmatched Sessions',
  MATCHED_SESSION: 'Matched Sessions',
  UNMATCHED_SESSION: 'Unmatched Sessions',
};

export const ExternalQuickLink = '_external';

export const Weekdays: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export interface BusinessHours {
  label: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

export const DefaultCallCenterOpenTime = '09:00:00';
export const DefaultCallCenterCloseTime = '17:00:00';
export const DayStartTime = '00:00:00';
export const DayEndTime = '23:59:59';
export const DayStartTimeLabel = '12:00 AM';
export const DayEndTimeLabel = '11:59 PM';

export function isCampaignSection(url: string) {
  let split_url = url.split('/');
  return (
    split_url.length > 0 &&
    split_url[1] === 'campaign' &&
    CampaignSideBarSections.some((item) => item.id === split_url[split_url.length - 1])
  );
}

export enum CampaignPolicyTabs {
  WrittenPolicies = 'written-policies',
  PolicyConfiguration = 'policy-configuration',
}
